/* eslint-disable react-refresh/only-export-components */
import { useEffect, useMemo, useState, useContext, createContext } from 'react';
import { I18n } from "i18n-js";
import i18next from '@/configurations/i18n';
import en from '@/constants/languages/en';
import tr from '@/constants/languages/tr';
import { ReactNode } from '@tanstack/react-router';

const i18nJs = new I18n({
  en,
  tr,
});
i18nJs.defaultSeparator = '|';

const getInitialLang = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('lang') || 'tr';
  }
  return 'tr';
};

import { Dispatch, SetStateAction } from 'react';

type LocalizeContextType = {
  I18n: I18n;
  i18next: typeof i18next;
  locale: string;
  setLocale: Dispatch<SetStateAction<string>>;
  toggleLanguage: () => void;
  t: (key: any) => string;
};

const LocalizeContext = createContext<LocalizeContextType>({
  I18n: i18nJs,
  i18next,
  locale: 'tr',
  setLocale: () => { },
  toggleLanguage: () => { },
  t: (key: any) => i18nJs.t(key, { defaultValue: key }),
});

const LocalizeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locale, setLocale] = useState(getInitialLang());

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('lang', locale);
    }
    i18nJs.locale = locale;
    i18next.changeLanguage(locale);
  }, [locale]);

  useEffect(() => {
    const lang = getInitialLang();
    setLocale(lang);
    i18nJs.locale = lang;
    i18next.changeLanguage(lang);
  }, []);

  const toggleLanguage = () => {
    setLocale(prevLocale => (prevLocale === 'en' ? 'tr' : 'en'));
  };

  const t = (key: any) => i18nJs.t(key, { defaultValue: key });

  const contextValue = useMemo(
    () => ({ I18n: i18nJs, i18next, locale, setLocale, toggleLanguage, t }),
    [locale]
  );
  return (
    <LocalizeContext.Provider value={contextValue}>
      {children}
    </LocalizeContext.Provider>
  );
};

export default LocalizeProvider;

export function useLocalizeContext() {
  const context = useContext(LocalizeContext)
  if (!context) {
    throw new Error('useLocalizeContext must be used within an LocalizeProvider')
  }
  return context
}
